/**
 * Guías paso a paso (lenguaje sencillo) para configurar correo del despacho.
 */

function GuideBox({ title, steps, testId }) {
  return (
    <div
      className="rounded-sm border border-slate-200 bg-white px-3 py-2.5 text-[11px] text-slate-700"
      data-testid={testId}
    >
      {title ? <p className="m-0 mb-1.5 font-semibold text-[#0F2D5C]">{title}</p> : null}
      <ol className="m-0 pl-4 list-decimal space-y-1 leading-relaxed">
        {(steps || []).map((step, idx) => (
          <li key={idx}>{step}</li>
        ))}
      </ol>
    </div>
  );
}

/** Guía del modo plataforma (lo más simple para el despacho). */
export function PlatformEmailSetupGuide() {
  return (
    <GuideBox
      testId="email-guide-platform"
      title="Cómo activar el correo (modo sencillo)"
      steps={[
        "Escriba el correo del despacho (donde quiere recibir las respuestas de sus clientes) y el nombre que verán.",
        "Marque «Activar envíos» y pulse Guardar. Eso solo autoriza el despacho a mandar; no configura el servidor.",
        "Si ve «Listo para enviar», use «Probar envío» con su propio correo.",
        "Si aparece que la plataforma aún no está activa: abra «Usar mi Gmail, Outlook…», elija Conectar Google (o Microsoft), autorice y Guarde. Alternativa: soporte activa el SMTP de BNT en el servidor.",
      ]}
    />
  );
}

/** Guía para el comprador/titular al elegir Conectar Google. */
export function GoogleBuyerSetupGuide({ oauthReady = false }) {
  if (oauthReady) {
    return (
      <GuideBox
        testId="email-guide-google-buyer"
        title="Cómo conectar su Gmail"
        steps={[
          "Pulse el botón «Conectar Google».",
          "Se abrirá la ventana oficial de Google. Elija la cuenta del despacho (recomendamos una solo para avisos).",
          "Pulse Permitir / Aceptar. No le pedimos su contraseña: Google solo autoriza el envío.",
          "Al volver aquí debe verse «Conectado como…». Luego pulse Guardar y Active envíos.",
          "Pruebe con «Probar envío» hacia su propio correo.",
        ]}
      />
    );
  }
  return (
    <GuideBox
      testId="email-guide-google-buyer"
      title="Antes de conectar Gmail"
      steps={[
        "Esta opción necesita que el operador BNT configure primero Google (panel amarillo «Conexión de la plataforma»).",
        "Mientras tanto puede usar «Correo de la plataforma» arriba: solo correo + Activar + Guardar.",
        "Cuando el operador termine, el botón «Conectar Google» se habilitará y podrá autorizar su cuenta en un clic.",
      ]}
    />
  );
}

/** Guía para el comprador/titular al elegir Conectar Microsoft. */
export function MicrosoftBuyerSetupGuide({ oauthReady = false }) {
  if (oauthReady) {
    return (
      <GuideBox
        testId="email-guide-microsoft-buyer"
        title="Cómo conectar Outlook / Hotmail / Live / Microsoft 365"
        steps={[
          "Pulse «Conectar Microsoft».",
          "Se abrirá la ventana oficial de Microsoft. Inicie sesión con la cuenta del despacho.",
          "Acepte los permisos. No escribimos su contraseña aquí: Microsoft autoriza el envío.",
          "Al volver debe verse «Conectado como…». Guarde y active envíos.",
          "Pruebe enviándose un correo desde «Probar envío».",
        ]}
      />
    );
  }
  return (
    <GuideBox
      testId="email-guide-microsoft-buyer"
      title="Antes de conectar Outlook / Microsoft"
      steps={[
        "El operador BNT debe configurar Microsoft en el panel amarillo «Conexión de la plataforma».",
        "Mientras tanto use «Correo de la plataforma» (más sencillo, sin contraseñas).",
        "Cuando esté listo, aparecerá el botón «Conectar Microsoft» y solo tendrá que aceptar en la ventana de Microsoft.",
      ]}
    />
  );
}

/** Guía SMTP / otro proveedor. */
export function SmtpBuyerSetupGuide() {
  return (
    <GuideBox
      testId="email-guide-smtp"
      title="Cómo configurar otro correo (SMTP)"
      steps={[
        "Use esta opción solo si su proveedor le dio datos de «servidor de correo saliente» (SMTP).",
        "Gmail: no use su contraseña normal. En Google → Seguridad → Contraseñas de aplicaciones, genere una de 16 caracteres y péguela aquí.",
        "Outlook, Hotmail o Live: no use esta opción. Elija «Mi Outlook / Hotmail / Live» y Conectar Microsoft.",
        "Indique usuario (casi siempre el correo completo), la contraseña de aplicación, pulse Guardar y «Probar SMTP».",
      ]}
    />
  );
}

/** Instrucciones del operador: de dónde salen Client ID y Secret. */
export function OperatorOauthSetupGuide({ googleRedirect = "", microsoftRedirect = "" }) {
  return (
    <div className="sm:col-span-2 space-y-3" data-testid="email-guide-operator-oauth">
      <GuideBox
        title="¿Qué son estos datos? (en palabras simples)"
        steps={[
          "El identificador de cliente es como el «número de folio» que Google o Microsoft le dan a Visor Fiscal para que pueda pedir permiso de envío.",
          "El secreto de cliente es como una «llave privada» que solo usted (operador) guarda. Nunca la comparta ni la ponga en WhatsApp.",
          "La primera casilla (URL larga) es la dirección de regreso: cópiela tal cual en la consola de Google o Microsoft. Si cambia una letra, no conectará.",
          "Después de guardar la conexión, cada despacho solo pulsa «Conectar Google» o «Conectar Microsoft» y acepta en la ventana oficial.",
        ]}
      />

      <div className="rounded-sm border border-amber-200 bg-white px-3 py-2.5 text-[11px] text-slate-700">
        <p className="m-0 mb-1.5 font-semibold text-amber-950">Google — cómo obtener el identificador y el secreto (paso a paso)</p>
        <ol className="m-0 pl-4 list-decimal space-y-1 leading-relaxed">
          <li>Entre a <strong>console.cloud.google.com</strong> con una cuenta Google (la de BNT / operador).</li>
          <li>Cree un proyecto o elija uno existente (arriba a la izquierda).</li>
          <li>En el menú busque <strong>APIs y servicios → Biblioteca</strong> y active la <strong>API de Gmail</strong>.</li>
          <li>Vaya a <strong>Pantalla de consentimiento</strong>: tipo Externo (o Interno si es Google Workspace). Complete el nombre de la aplicación y su correo de soporte.</li>
          <li>En <strong>Credenciales → Crear credenciales → ID de cliente</strong>, elija <strong>Aplicación web</strong>.</li>
          <li>
            En «URI de redirección autorizados» pegue exactamente esta dirección:
            <span className="mt-1 block break-all font-mono text-[10px] bg-slate-50 border border-slate-200 rounded-sm px-1.5 py-1">
              {googleRedirect || "(se muestra arriba en el campo de Google)"}
            </span>
          </li>
          <li>Al crear, Google le muestra el <strong>identificador de cliente</strong> y el <strong>secreto del cliente</strong>. Cópielos a las casillas de la izquierda y pulse <strong>Guardar conexión</strong>.</li>
          <li>Si la aplicación está en modo Prueba, agregue como usuarios de prueba las cuentas de Gmail que van a conectar los despachos.</li>
        </ol>
      </div>

      <div className="rounded-sm border border-amber-200 bg-white px-3 py-2.5 text-[11px] text-slate-700">
        <p className="m-0 mb-1.5 font-semibold text-amber-950">Microsoft — cómo obtener el identificador y el secreto (paso a paso)</p>
        <ol className="m-0 pl-4 list-decimal space-y-1 leading-relaxed">
          <li>Entre a <strong>portal.azure.com</strong> → <strong>Microsoft Entra ID</strong> (antes Azure Active Directory).</li>
          <li>Vaya a <strong>Registros de aplicaciones → Nuevo registro</strong>.</li>
          <li>Nombre: por ejemplo «Visor Fiscal Correo». En tipos de cuenta elija la opción que incluya cuentas personales y de organización (para Outlook, Hotmail, Live y 365).</li>
          <li>
            En URI de redirección elija plataforma <strong>Web</strong> y pegue exactamente:
            <span className="mt-1 block break-all font-mono text-[10px] bg-slate-50 border border-slate-200 rounded-sm px-1.5 py-1">
              {microsoftRedirect || "(se muestra arriba en el campo de Microsoft)"}
            </span>
          </li>
          <li>Tras crear, copie el <strong>Id. de aplicación (cliente)</strong> → va en el identificador de cliente de Microsoft.</li>
          <li>En <strong>Certificados y secretos → Nuevo secreto de cliente</strong>, cree uno, copie el <strong>Valor</strong> (solo se ve una vez) → va en el secreto de cliente.</li>
          <li>En <strong>Permisos de la API</strong> agregue permisos delegados de Microsoft Graph: <strong>User.Read</strong> (leer el perfil), <strong>Mail.Send</strong> (enviar correo) y <strong>offline_access</strong> (mantener la sesión). Si su organización lo pide, conceda el consentimiento de administrador.</li>
          <li>Deje el inquilino en <strong>common</strong> salvo que soporte le indique otro. Pulse <strong>Guardar conexión</strong>.</li>
        </ol>
      </div>
    </div>
  );
}

export { GuideBox };
